import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Box, Stepper, Step, StepButton, Stack, Grid } from '@mui/material';
import PatientDemographics from '../patient/PatientDemographics';
import PatientReferralDocuments from '../patient/PatientReferralDocuments';
import FindClinic from './FindClinic';
import { useLazyQuery, useQuery } from '@apollo/client';
import { isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';
import { MyButton } from '../common/components';
import ReferralDetails from './ReferralDetails';
import { useSendReferral } from './redux/sendReferral';
import Swal from 'sweetalert2';
import { GET_REFERRAL_BY_UUID } from './gql';
import clientGraphql from '../../common/apollo-graphql';
import AuthContext from '../auth/context/AuthContext';
import {
  createPatientValidationSchema,
  createReferralValidationSchema,
  initialValues,
} from '../patient/PatientDemographicsForm';
import { GET_CLINIC_REFERRAL_FORM_ATTRIBUTES, MANAGE_CLINIC_DETAILS_QUERY } from '../clinics/gql/Query';
import {
  DEFAULT_INTERNAL_REFERRAL_FORM_FIELD,
  DEFAULT_REFERRAL_FORM_FIELD,
} from '../common/services/DefaultValuesUtils';
import * as yup from 'yup';
import { useFaxSend } from './redux/faxSend';
import HelperUtils from '../common/services/HelperUtils';
import { useToggleLeftDrawer } from '../common/redux/toggleLeftDrawer';
import mergeHelpText from '../../common/helpers/MergeHelpText';
import { composeSmsMessage, showReferralSentDialog } from './ReferralSentDialog';
import { useGetPatientPhoneNumber } from './patient/PatientDetails';
// import { ROLE_PROVIDER } from '../auth/constants/Role';

const steps = ['Find Provider', 'Patient Demographics', 'Referral Documents', 'Send Referral'];
const steps_index = { clinic: 0, patient: 1, documents: 2, referral: 3 };

export default function CreateReferral({ history, showOnlyPatientDemographics = false }) {
  let { id } = useParams();
  const [isFax, setIsFax] = useState(false);
  // const [isOrder, setIsOrder] = useState(false);
  const [isSaveDraft, setIsSaveDraft] = useState(false);
  const [referralUuid, setReferralUuid] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [patientDetails, setPatientDetails] = useState({});
  const [referralDetails, setReferralDetails] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});
  const [isSameOrg, setIsSameOrg] = useState(false);
  const [disableSend, setDisableSend] = useState(true);
  const [disableNext, setDisableNext] = useState(true);
  const [referralSchema, setReferralSchema] = useState({});
  const [patientSchema, setPatientSchema] = useState({});
  const [requiredCustom, setRequiredCustom] = useState(null);
  const [lastRequiredIndex, setLastRequiredIndex] = useState(-1);
  const [initialReferralValues, setInitialReferralValues] = useState({ ...initialValues });
  const [isFaxSending, setIsFaxSending] = useState(false);
  const currentUser = useContext(AuthContext);
  const [providers, setProviders] = useState([]);
  const { getPatientPhoneNumber } = useGetPatientPhoneNumber();

  // const SAVE_REFERRAL_AS_PENDING_APPROVAL = gql`
  //   mutation ($params: String!) {
  //     save_referral_as_pending_approval(params: $params)
  //   }
  // `;

  // const [saveReferralAsPendingApproval] = useMutation(SAVE_REFERRAL_AS_PENDING_APPROVAL, {
  //   client: clientGraphql,
  // });

  const { data: dataClinic } = useQuery(MANAGE_CLINIC_DETAILS_QUERY, {
    client: clientGraphql,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { id: currentUser.clinics[0].id }
  });

  const { sendReferral, sendReferralPending, dismissSendReferralError } = useSendReferral();
  const { faxSend, faxSendPending } = useFaxSend();
  const [getClinicReferralFormSettings] = useLazyQuery(
    GET_CLINIC_REFERRAL_FORM_ATTRIBUTES,
    {
      client: clientGraphql,
      variables: { id: id, formType: 'internal' },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
    },
  );

  const { toggleLeftDrawer } = useToggleLeftDrawer();

  useEffect(() => {
    if (!isEmpty(id) && isEmpty(referralUuid)) {
      setIsSaveDraft(true);
      setReferralUuid(id);
    }
  }, [id, referralUuid]);

  const handleOnSetReferralId = useCallback(
    uuid => {
      var tempUuid = referralUuid;
      tempUuid = uuid;
      setReferralUuid(tempUuid);
    },
    [setReferralUuid, referralUuid],
  );

  const handleOnDisableSendReferral = useCallback(
    isDisabled => {
      if (
        !isEmpty(currentUser.organization) &&
        !isEmpty(parsedData) &&
        parsedData.clinic.organization.toLowerCase() === currentUser.organization.toLowerCase()
      ) {
        setIsSameOrg(true);
        setDisableSend(false);
      } else if (isDisabled !== disableSend) {
        setDisableSend(isDisabled);
      }
    },
    [setDisableSend, currentUser, parsedData, disableSend],
  );

  const handleOnDisableNext = useCallback(
    isDisabled => {
      setDisableNext(isDisabled);
    },
    [setDisableNext],
  );

  const { data } = useQuery(GET_REFERRAL_BY_UUID, {
    variables: {
      uuid: referralUuid,
    },
    skip: !referralUuid,
    client: clientGraphql,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    refetchOnWindowFocus: false,
  });

  const isLastStep = useCallback(() => {
    return activeStep === steps.length - 1;
  }, [activeStep]);

  const isStepRequiredDataEmpty = () => {
    var disabled = false;
    if (activeStep === 0 && isEmpty(parsedData)) {
      disabled = true;
    }

    if (activeStep === steps_index.documents && disableSend) {
      disabled = true;
    }

    return disabled;
  };

  const handleNext = useCallback(() => {
    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    const newActiveStep = isLastStep() ? activeStep : activeStep + 1;
    setActiveStep(newActiveStep);
  }, [
    activeStep,
    completed,
    isLastStep,
  ]);

  const handleBack = useCallback(() => {
    if (activeStep === steps_index.clinic) {
      toggleLeftDrawer(true);
      history.replace('/referrals/active');
    } else {
      setActiveStep(prevActiveStep => prevActiveStep - 1);
    }
  }, [activeStep, history, toggleLeftDrawer]);

  const handleStep = step => () => {
    setActiveStep(step);
  };

  const onSendReferral = useCallback(async () => {
    // this is for testing only
    // const {patientPhoneNumber} = await getPatientPhoneNumber(referralUuid);
    // console.log('onSendReferral referralDetails=',referralDetails,' patientDetails=',patientDetails);
    // const smsMessage = composeSmsMessage(
    //     referralDetails.referral.referrer,
    //     referralDetails.referral.receiver
    // );
    // showReferralSentDialog(
    //     'Referral Sent!',
    //     {phone: patientPhoneNumber, smsMessage, referralUuid},
    //     () => {
    //       console.log('sms onsuccess!');
    //     }
    // );
    // return;

    dismissSendReferralError();
    setIsFaxSending(true);

    const handleFaxSend = res => {
      const formData = new FormData();
      const faxNumber = HelperUtils.getFaxNumber(referralDetails.referral);
      formData.append('file', res);
      formData.append('recipient', '001' + faxNumber.replace('-', ''));
      formData.append('referral_uuid', referralUuid);

      return faxSend(formData);
    };

    const handleSendReferral = () => {
      return sendReferral(referralUuid);
    };

    const showSuccessMessage = async (message) => {
      const { patientPhoneNumber } = await getPatientPhoneNumber(referralUuid);

      const smsMessage = composeSmsMessage(
        referralDetails.referral.referrer,
        referralDetails.referral.receiver
      );

      showReferralSentDialog(
        message,
        { phone: patientPhoneNumber, smsMessage, referralUuid },
        () => {
          toggleLeftDrawer(true);
          history.push('/referrals/active');
        },
        referralDetails.referral.is_order
      );
    };

    const showErrorMessage = message => {
      Swal.close();
      Swal.fire({
        icon: 'error',
        text: message,
        showConfirmButton: true,
      });
    };

    // if (referralDetails.referral.is_order) {
    //   return saveReferralAsPendingApproval({
    //     variables: {
    //       params: JSON.stringify({
    //         uuid: referralUuid,
    //       }),
    //     },
    //   })
    //     .then(data => {
    //       showReferralSentDialog(
    //         "Order saved",
    //         {},
    //         () => {
    //           toggleLeftDrawer(true);
    //           history.push('/referrals/active');
    //         },
    //         true
    //       );
    //     })
    //     .catch(error => showErrorMessage(error.data ? error.data : error.message))
    //     .finally(() => setIsFaxSending(false));
    // }
    if (referralDetails.referral.is_fax) {
      Swal.fire({
        title: 'Validating Files for Faxing',
        text: 'Please Wait',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      new Promise(resolve => setTimeout(resolve, 2000))
        .then(res =>
          handleSendReferral().then(data => ({ res, data }))
        )
        .then(({ res, data }) => {
          showSuccessMessage(data.message);
          return res;
        })
        .then(res => { handleFaxSend(res); })
        .catch(error => { showErrorMessage(error.data ? error.data.error : error.message); })
        .finally(() => {
          setIsFaxSending(false);
        });

    } else {
      sendReferral(referralUuid)
        .then(data => {
          showSuccessMessage(data.message);
        })
        .catch(error => showErrorMessage(error.data ? error.data : error.message))
        .finally(() => setIsFaxSending(false));
    }
  }, [
    sendReferral,
    // saveReferralAsPendingApproval,
    referralUuid,
    history,
    dismissSendReferralError,
    faxSend,
    referralDetails,
    toggleLeftDrawer,
    getPatientPhoneNumber,
  ]);

  const handleCustomFormAttributes = useCallback(formAttrs => {
    var initialVals = { ...initialValues };
    var refSchema = {};
    var patSchema = {};
    var _lastRequiredIndex = -1;
    var form_attributes = formAttrs;

    form_attributes.forEach((custom_field, index) => {
      if (custom_field.required) {
        _lastRequiredIndex = index;
      }
      if (custom_field.field in createPatientValidationSchema && custom_field.required) {
        patSchema[custom_field.field] = createPatientValidationSchema[custom_field.field];
      }
      if (custom_field.field in createReferralValidationSchema && custom_field.required) {
        refSchema[custom_field.field] = createReferralValidationSchema[custom_field.field];
      }
      if (custom_field.field.includes('custom_') && custom_field.kind !== 'multiple_choice') {
        initialVals.referral[custom_field.field] = '';
      } else if (
        custom_field.field.includes('custom_') &&
        custom_field.kind === 'multiple_choice'
      ) {
        initialVals.referral[custom_field.field] = [];
      }
      if (
        custom_field.field.includes('custom_') &&
        custom_field.required &&
        custom_field.kind === 'multiple_choice'
      ) {
        refSchema[custom_field.field] = yup.array().min(1, custom_field.label + ' is required');
      } else if (
        custom_field.field.includes('custom_') &&
        custom_field.required &&
        custom_field.kind !== 'multiple_choice'
      ) {
        refSchema[custom_field.field] = yup.string().required(custom_field.label + ' is required');
      }
    });
    setLastRequiredIndex(_lastRequiredIndex);
    setPatientSchema(patSchema);
    setReferralSchema(refSchema);
    setInitialReferralValues(initialVals);
    setRequiredCustom(form_attributes.slice());
  }, []);

  const handleOnClickClinic = useCallback(
    data => {
      var isOrg =
        !isEmpty(currentUser.organization) &&
        !isEmpty(data) &&
        !isEmpty(data.row.clinic.organization) &&
        !isEmpty(data.row.clinic.organization.name) &&
        data.row.clinic.organization.name.toLowerCase() === currentUser.organization.toLowerCase();
      getClinicReferralFormSettings({
        client: clientGraphql,
        variables: { id: data.row.clinic.id, formType: isOrg ? 'internal' : 'external' },
      })
        .then(res => {
          setIsFax(isEmpty(data.row.clinic.clinic_users));
          // setIsOrder(data.row.clinic.is_order);
          setParsedData({
            ...parsedData,
            specialty: {
              name: data.row.specializations.map(item => item.name).join(', '),
            },
            clinic: {
              isFax: isEmpty(data.row.clinic.clinic_users),
              provider_id: data.row.id,
              provider_name: data.row.display_name,
              organization: !isEmpty(data.row.clinic.organization)
                ? data.row.clinic.organization.name
                : '',
              name: data.row.clinic.name,
              id: data.row.clinic.id,
              is_order: data.row.clinic.is_order,
              address: data.row.clinic.full_address,
              documents: data.row.requiredDocuments,
              optionalDocuments: data.row.optionalDocuments,
            },
          });
          setIsSameOrg(isOrg);

          let defaultFields = isOrg ? DEFAULT_INTERNAL_REFERRAL_FORM_FIELD : DEFAULT_REFERRAL_FORM_FIELD;

          if (!isEmpty(res.data) && !isEmpty(res.data.clinic_custom_form_attributes)) {
            let form_attributes = res.data.clinic_custom_form_attributes.filter(
              attr => !attr.hidden,
            );

            form_attributes = isOrg ? form_attributes.sort((a, b) => b.required - a.required) : form_attributes;

            handleCustomFormAttributes(
              mergeHelpText(
                form_attributes,
                defaultFields
              )
            );
          } else {
            handleCustomFormAttributes(
              defaultFields
            );
          }

          handleNext();
        })
        .catch(err => {
          console.log(err);
        });
    },
    [
      handleNext,
      parsedData,
      currentUser,
      setIsSameOrg,
      getClinicReferralFormSettings,
      handleCustomFormAttributes,
      setIsFax,
    ],
  );

  useEffect(() => {
    if (!isEmpty(dataClinic?.clinic?.providers)) {
      let filteredProviders = dataClinic.clinic.providers.filter(
        (provider) => !provider.is_default
      );

      // if (parsedData?.clinic?.is_order) {
      //   filteredProviders = filteredProviders.filter(
      //     (provider) => provider.has_user_provider
      //   );
      // }

      setProviders(filteredProviders);
    }
  }, [dataClinic, parsedData]);

  useEffect(() => {
    if (!isEmpty(data) && isEmpty(parsedData)) {
      const { referral } = data;
      var isOrg =
        !isEmpty(currentUser.organization) &&
        !isEmpty(referral) &&
        !isEmpty(referral.receiver.organization) &&
        !isEmpty(referral.receiver.organization.name) &&
        referral.receiver.organization.name.toLowerCase() ===
        currentUser.organization.toLowerCase();
      getClinicReferralFormSettings({
        client: clientGraphql,
        variables: { id: referral.receiver.id, formType: isOrg ? 'internal' : 'external' },
      })
        .then(res => {
          let defaultFields = isOrg ? DEFAULT_INTERNAL_REFERRAL_FORM_FIELD : DEFAULT_REFERRAL_FORM_FIELD;

          if (!isEmpty(res.data) && !isEmpty(res.data.clinic_custom_form_attributes)) {
            var form_attributes = res.data.clinic_custom_form_attributes.filter(
              attr => !attr.hidden,
            );

            form_attributes = isOrg ? form_attributes.sort((a, b) => b.required - a.required) : form_attributes;

            handleCustomFormAttributes(
              mergeHelpText(
                form_attributes,
                defaultFields
              )
            );
          } else {
            handleCustomFormAttributes(
              defaultFields,
            );
          }
          setIsFax(referral.is_fax);
          setParsedData({
            specialty: {
              name: referral.provider.specializations.map(item => item.name).join(', '),
            },
            clinic: {
              isFax: referral.is_fax,
              provider_id: referral.provider.id,
              provider_name: referral.provider.display_name,
              organization: !isEmpty(referral.receiver.organization)
                ? referral.receiver.organization.name
                : '',
              name: referral.receiver.name,
              id: referral.receiver.id,
              address: referral.receiver.full_address,
              documents: referral.provider.documents.filter(doc => {
                return doc.required;
              }),
              optionalDocuments: referral.provider.documents.filter(doc => {
                return !doc.required;
              }),
            },
            status: referral.status,
          });
          setIsSameOrg(isOrg);
          const newCompleted = completed;
          newCompleted[0] = true;
          setCompleted(newCompleted);
          setActiveStep(!isEmpty(id) && (activeStep === 0 || activeStep === 1) ? 1 : activeStep);
        })
        .catch(err => {
          console.log(err);
        });
    }
  }, [
    data,
    id,
    completed,
    activeStep,
    parsedData,
    currentUser,
    setIsSameOrg,
    getClinicReferralFormSettings,
    handleCustomFormAttributes,
    setIsFax,
  ]);

  if (!parsedData && referralUuid) {
    return null;
  }

  if (showOnlyPatientDemographics) {
    return (
      <Box sx={{ padding: 2 }}>
        <PatientDemographics
          prefix="patient"
          parsedData={parsedData}
          referralUuid={referralUuid}
          onSetReferralId={handleOnSetReferralId}
          onDisableNext={handleOnDisableNext}
          isSameOrg={isSameOrg}
          isSavedDraft={isSaveDraft}
          referralSchema={referralSchema}
          patientSchema={patientSchema}
          requiredCustom={requiredCustom}
          initialValues={initialReferralValues}
          lastRequiredIndex={lastRequiredIndex}
          providers={providers}
          showOnlyPatientDemographics={showOnlyPatientDemographics}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper nonLinear activeStep={activeStep}>
        {steps.map((label, index) => (
          <Step key={label} completed={completed[index]}>
            <StepButton
              color="inherit"
              onClick={handleStep(index)}
              disabled={
                isEmpty(parsedData) || (activeStep >= steps_index.patient && isEmpty(referralUuid))
              }
            >
              {label}
            </StepButton>
          </Step>
        ))}
      </Stepper>
      <div>
        <React.Fragment>
          <Grid container justifyContent="center" alignItems="center">
            <Grid item md={12} xs={12} sx={{ px: 2, mt: 3, py: 2 }}>
              {activeStep === steps_index.clinic && (
                <FindClinic handleOnClickClinic={handleOnClickClinic} />
              )}

              {activeStep === steps_index.patient &&
                !isEmpty(requiredCustom) &&
                !isEmpty(parsedData) && (
                  <PatientDemographics
                    prefix="patient"
                    parsedData={parsedData}
                    referralUuid={referralUuid}
                    onSetReferralId={handleOnSetReferralId}
                    onDisableNext={handleOnDisableNext}
                    isSameOrg={isSameOrg}
                    isSavedDraft={isSaveDraft}
                    referralSchema={referralSchema}
                    patientSchema={patientSchema}
                    requiredCustom={requiredCustom}
                    initialValues={initialReferralValues}
                    lastRequiredIndex={lastRequiredIndex}
                    providers={providers}
                  />
                )}
              {activeStep === steps_index.documents && !isEmpty(parsedData) && (
                <PatientReferralDocuments
                  referralUuid={referralUuid}
                  isSameOrg={isSameOrg}
                  onDisableSendReferral={handleOnDisableSendReferral}
                  parsedData={parsedData}
                />
              )}
              {activeStep === steps_index.referral && (
                <ReferralDetails
                  referralId={referralUuid}
                  onDisableSendReferral={handleOnDisableSendReferral}
                  setCreateFileObjects={() => null}
                  referralDetails={referralDetails}
                  setReferralDetails={setReferralDetails}
                  patientDetails={patientDetails}
                  setPatientDetails={setPatientDetails}
                  isReferrer={true}
                />
              )}
              <Stack direction="row" spacing={1} sx={{ pt: 2 }}>
                <MyButton
                  color="primary"
                  variant="outlined"
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                  fullWidth={false}
                >
                  {activeStep === steps_index.clinic ? 'Exit' : 'Back'}
                </MyButton>
                <Box sx={{ flex: '1 1 auto' }} />
                {!isLastStep() && (<MyButton
                  onClick={handleNext}
                  disabled={
                    (activeStep === steps_index.patient && disableNext) ||
                    isStepRequiredDataEmpty() ||
                    (activeStep >= steps_index.patient && isEmpty(referralUuid))
                  }
                  color="primary"
                  variant="outlined"
                  sx={{ mr: 1 }}
                  fullWidth={false}
                >
                  Next
                </MyButton>)}
                {isLastStep() ? (
                  <MyButton
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={isEmpty(referralUuid) || disableSend || isFaxSending}
                    loading={sendReferralPending || faxSendPending || isFaxSending}
                    onClick={onSendReferral}
                    fullWidth={false}
                  >
                    {isFax ? 'Send Referral Fax' : 'Send Referral'}
                  </MyButton>
                ) : null}
              </Stack>
            </Grid>
          </Grid>
        </React.Fragment>
      </div>
    </Box>
  );
}
